import { useEffect, useState } from "react";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";

/**
 * ⌘K / Ctrl+K launcher. Enter opens the highlighted app in a new tab;
 * categories jump to the filtered dashboard view.
 */
export default function CommandPalette({ apps = [], categories = [], onSelectCategory }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key?.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    const run = (fn) => {
        setOpen(false);
        fn();
    };

    const categoryName = (id) => categories.find((c) => c.id === id)?.name;

    return (
        <CommandDialog open={open} onOpenChange={setOpen}>
            <CommandInput placeholder="Search apps and categories..." data-testid="command-input" />
            <CommandList>
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup heading="Apps">
                    {apps.map((a) => (
                        <CommandItem
                            key={a.id}
                            // cmdk matches on this string, so fold in the searchable fields.
                            value={`${a.name} ${a.host} ${categoryName(a.category_id) || ""} ${(a.tags || []).join(" ")}`}
                            onSelect={() =>
                                run(() => window.open(a.url, "_blank", "noopener,noreferrer"))
                            }
                            data-testid={`command-app-${a.id}`}
                        >
                            <span
                                className={`mr-2 h-2 w-2 rounded-full shrink-0 ${
                                    a.last_status === "online"
                                        ? "bg-emerald-500"
                                        : a.last_status === "offline"
                                          ? "bg-red-500"
                                          : "bg-muted-foreground/40"
                                }`}
                            />
                            <span className="truncate">{a.name}</span>
                            <span className="ml-auto pl-3 text-xs text-muted-foreground font-mono truncate">
                                {a.host}:{a.port}
                            </span>
                        </CommandItem>
                    ))}
                </CommandGroup>
                {categories.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Categories">
                            {categories.map((c) => (
                                <CommandItem
                                    key={c.id}
                                    value={`category ${c.name}`}
                                    onSelect={() => run(() => onSelectCategory?.(c.id))}
                                    data-testid={`command-category-${c.id}`}
                                >
                                    <span className="mr-2">{c.icon}</span>
                                    {c.name}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}
            </CommandList>
        </CommandDialog>
    );
}
